/**
 * Featured Variant Card
 * Custom element for variant cards with AJAX add-to-cart functionality
 */

class FeaturedVariantCard extends HTMLElement {
  constructor() {
    super();
    this.variantId = null;
    this.productId = null;
    this.action = null;
  }

  connectedCallback() {
    // Get data attributes - parse variantId as integer for Shopify API
    this.variantId = parseInt(this.dataset.variantId, 10);
    this.productId = parseInt(this.dataset.productId, 10);
    this.action = this.dataset.action || 'add_to_cart';

    // Initialize based on action
    if (this.action === 'add_to_cart') {
      this.setupAddToCart();
    }
  }

  /**
   * Setup add-to-cart button event listener
   */
  setupAddToCart() {
    const button = this.querySelector('[data-add-to-cart]');
    if (!button) return;

    button.addEventListener('click', () => this.handleAddToCart());
  }

  /**
   * Get quantity from input or default to 1
   */
  getQuantity() {
    const qtyInput = this.querySelector('[data-quantity]');
    if (qtyInput) {
      const qty = parseInt(qtyInput.value);
      return isNaN(qty) || qty < 1 ? 1 : qty;
    }
    return 1;
  }

  /**
   * Handle add-to-cart button click
   */
  async handleAddToCart() {
    const button = this.querySelector('[data-add-to-cart]');
    if (!button || button.disabled) return;

    // Validate variant ID
    if (!this.variantId) {
      console.error('No variant ID found');
      return;
    }

    // Get quantity
    const quantity = this.getQuantity();

    // Disable button + show loading state
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.classList.add('loading');

    try {
      // AJAX add-to-cart request
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || 'Failed to add to cart');
      }

      // Success: Refresh cart drawer and open it
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
        detail: { opener: button }
      }));

      // Show success state
      button.classList.remove('loading');
      button.classList.add('success');
      button.textContent = 'Added!';

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('success');
        button.disabled = false;
      }, 1500);

    } catch (error) {
      console.error('Add to cart error:', error);

      // Show error state
      button.classList.remove('loading');
      button.classList.add('error');
      button.textContent = 'Error - Try Again';

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('error');
        button.disabled = false;
      }, 2000);
    }
  }
}

// Register custom element
if (!customElements.get('featured-variant-card')) {
  customElements.define('featured-variant-card', FeaturedVariantCard);
}
